import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth';
import { getAccessToken, isRefreshTokenExpired } from '@/lib/token-manager';
import { useAutoLogout } from '@/hooks/useAutoLogout';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string[];
}

function SessionExpiryBanner({
  onExtend,
  onDismiss,
}: {
  onExtend: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        background: '#1c1c1e',
        color: '#fff',
        borderRadius: 12,
        padding: '14px 20px',
        display: 'flex',
        gap: 16,
        alignItems: 'center',
        boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
        maxWidth: 480,
        width: 'calc(100% - 32px)',
        animation: 'wlSlideUp .25s ease',
      }}
    >
      <span style={{ fontSize: 13.5, lineHeight: 1.4, flex: 1 }}>
        Your session will expire in about 5 minutes. Stay signed in?
      </span>
      <button
        onClick={onExtend}
        style={{
          background: 'var(--teal)',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          padding: '7px 14px',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        Stay signed in
      </button>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'rgba(255,255,255,0.5)',
          fontSize: 18,
          cursor: 'pointer',
          lineHeight: 1,
          padding: '0 2px',
        }}
      >
        ×
      </button>
    </div>
  );
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { user, hasRole } = useAuthStore();
  const location = useLocation();
  const { showingExpiryWarning, extendSession } = useAutoLogout();
  const [dismissed, setDismissed] = React.useState(false);

  const hasToken = getAccessToken() !== null || !isRefreshTokenExpired();

  if (!hasToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && user) {
    const hasRequiredRole = Array.isArray(requiredRole)
      ? hasRole(requiredRole)
      : hasRole([requiredRole]);

    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  const handleExtend = () => {
    setDismissed(false);
    extendSession();
  };

  return (
    <>
      {children}
      {showingExpiryWarning && !dismissed && (
        <SessionExpiryBanner onExtend={handleExtend} onDismiss={() => setDismissed(true)} />
      )}
    </>
  );
};

interface PublicRouteProps {
  children: React.ReactNode;
}

export const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const hasToken = getAccessToken() !== null;
  const user = useAuthStore((state) => state.user);

  if (hasToken && user) {
    return <Navigate to="/personal/dashboard" replace />;
  }

  return <>{children}</>;
};
